import { Briefcase, CheckCircle2, MapPin, GraduationCap, Monitor, Users, Layers, Globe } from "lucide-react";
import type { SubjectView } from "@/services/marketplaceService";
import { currencySymbol } from "@/lib/currency";
import { formatTeacherLocation } from "@/lib/utils";
import { SubscriptionTierBadge } from "@/components/teacher/SubscriptionTierBadge";

interface ProfileHeroProps {
  fullName: string;
  handle: string;
  /** Active subscription tier; null when the teacher has no active subscription */
  teacherTier?: 'SILVER' | 'GOLD' | 'PLATINUM' | null;
  profileImage?: string | null;
  subjects: SubjectView[];
  locationDisplayName?: string | null;
  city?: string | null;
  country?: string | null;
  yearsTeachingExperience?: string | null;
  highestQualification?: string | null;
  overallStatus?: string | null;
  offeringsCount?: number;
  minPrice?: number | null;
  minPriceCurrency?: string | null;
  teachingModes?: string[];
  instructionLanguages?: string[];
}

const QUAL_LABELS: Record<string, string> = {
  BELOW_SECONDARY: "Below Secondary",
  SECONDARY: "Secondary",
  DIPLOMA: "Diploma",
  BACHELOR: "Bachelor's",
  MASTER: "Master's",
  DOCTORATE: "PhD",
  OTHER: "Graduate",
};

const EXP_LABELS: Record<string, string> = {
  LESS_THAN_1: "< 1 yr exp",
  ONE_TO_3: "1–3 yrs exp",
  THREE_TO_5: "3–5 yrs exp",
  FIVE_TO_10: "5–10 yrs exp",
  MORE_THAN_10: "10+ yrs exp",
};

const modeConfig: Record<string, { icon: typeof Monitor; label: string; bg: string; text: string }> = {
  ONLINE:  { icon: Monitor, label: "Online",  bg: "bg-white/15",  text: "text-white/90" },
  OFFLINE: { icon: Users,   label: "Offline", bg: "bg-white/10",  text: "text-white/80" },
  HYBRID:  { icon: Layers,  label: "Hybrid",  bg: "bg-white/12",  text: "text-white/85" },
};

export function ProfileHero({
  fullName,
  handle,
  teacherTier = null,
  profileImage,
  subjects,
  locationDisplayName,
  city,
  country,
  yearsTeachingExperience,
  highestQualification,
  overallStatus,
  offeringsCount,
  minPrice,
  minPriceCurrency,
  teachingModes = [],
  instructionLanguages = [],
}: ProfileHeroProps) {
  const isVerified = overallStatus === "VERIFIED" || overallStatus === "APPROVED";
  const locationText = formatTeacherLocation(locationDisplayName, city, null, country);
  const subjectNames = subjects.map(s => s.subjectName).filter(Boolean);
  // SCRUM-365: backend currency only — no literal "$" fallback.
  const priceSymbol = currencySymbol(minPriceCurrency);
  const formattedPrice = minPrice != null ? Math.round(minPrice).toLocaleString() : null;
  const qualLabel = highestQualification ? (QUAL_LABELS[highestQualification] ?? highestQualification) : null;
  const expLabel = yearsTeachingExperience ? (EXP_LABELS[yearsTeachingExperience] ?? yearsTeachingExperience) : null;

  return (
    <div className="rounded-3xl overflow-hidden mb-8 shadow-xl">
      {/* ── Background with mesh gradient ── */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Decorative blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[hsl(237,52%,31%)]/30 blur-[80px]" />
          <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-[hsl(39,96%,48%)]/15 blur-[70px]" />
        </div>

        <div className="relative px-6 pt-8 pb-6 flex flex-col sm:flex-row gap-6 items-start">

          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="p-[3px] rounded-2xl bg-gradient-to-br from-white/40 to-white/10 shadow-2xl">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={fullName}
                  className="w-28 h-28 sm:w-36 sm:h-36 rounded-[13px] object-cover block"
                />
              ) : (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-[13px] bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center">
                  <span className="text-4xl font-bold text-white/90">
                    {fullName?.charAt(0)?.toUpperCase() ?? "T"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 pt-1">

            {/* Name + verified */}
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">{fullName}</h1>
              {isVerified && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              )}
              <SubscriptionTierBadge tier={teacherTier} />
            </div>

            {/* Handle */}
            {handle && <p className="text-sm text-white/40 mb-3">@{handle}</p>}

            {/* Subject chips */}
            {subjectNames.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {subjectNames.slice(0, 4).map(s => (
                  <span key={s} className="text-sm font-semibold px-3 py-1 rounded-full bg-white/10 text-white border border-white/15 backdrop-blur-sm">
                    {s}
                  </span>
                ))}
                {subjectNames.length > 4 && (
                  <span className="text-sm text-white/40 font-medium self-center">+{subjectNames.length - 4} more</span>
                )}
              </div>
            )}

            {/* Meta pills row */}
            <div className="flex flex-wrap items-center gap-2">
              {expLabel && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[hsl(39,96%,48%)]/20 text-[hsl(39,96%,80%)] border border-[hsl(39,96%,48%)]/25">
                  <Briefcase className="w-3 h-3" />
                  {expLabel}
                </span>
              )}
              {qualLabel && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 text-white/80 border border-white/15">
                  <GraduationCap className="w-3 h-3" />
                  {qualLabel}
                </span>
              )}
              {locationText && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 text-white/70 border border-white/15">
                  <MapPin className="w-3 h-3" />
                  {locationText}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom stats strip ── */}
        {(formattedPrice || (offeringsCount != null && offeringsCount > 0) || teachingModes.length > 0 || instructionLanguages.length > 0) && (
          <div className="relative flex flex-wrap items-center gap-4 px-6 py-4 border-t border-white/10 bg-black/30 backdrop-blur-sm">

            {/* Price */}
            {formattedPrice && (
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-white">{priceSymbol}{formattedPrice}</span>
                <span className="text-xs text-white/40 font-medium">/ session</span>
              </div>
            )}

            {/* Offerings count */}
            {offeringsCount != null && offeringsCount > 0 && (
              <span className="text-sm text-white/50 font-medium">
                {offeringsCount} offering{offeringsCount !== 1 ? "s" : ""}
              </span>
            )}

            {/* Divider */}
            {(formattedPrice || offeringsCount) && (teachingModes.length > 0 || instructionLanguages.length > 0) && (
              <div className="w-px h-5 bg-white/15 hidden sm:block" />
            )}

            {/* Teaching modes + languages */}
            <div className="flex flex-wrap gap-2 ml-auto">
              {instructionLanguages.filter(l => l !== "OTHER").map(lang => (
                <span key={lang} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 text-white/80 border border-white/10">
                  <Globe className="w-3 h-3 flex-shrink-0" />
                  {lang}
                </span>
              ))}
              {teachingModes.map(m => {
                const cfg = modeConfig[m];
                if (!cfg) return null;
                const Icon = cfg.icon;
                return (
                  <span key={m} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-white/10 ${cfg.bg} ${cfg.text}`}>
                    <Icon className="w-3 h-3 flex-shrink-0" />
                    {cfg.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
